// App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import SavingsTracker from './components/SavingsTracker';
import Success from './pages/Success';
import Cancel from './pages/Cancel';
import UserDashboard from './components/UserDashboard';
import Login from './components/Login';
import TermsAndConditions from './pages/TermsAndConditions'; // Import the new Terms component

/**
 * ProtectedRoute Component
 * Higher-order component that protects routes requiring authentication
 * Redirects to login if no valid token is found in localStorage
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @returns {React.ReactNode} Protected route or redirect to login
 */
const ProtectedRoute = ({ children }) => {
    // Check for authentication token in localStorage
    const token = localStorage.getItem('userToken');
    
    // Redirect to login if no token found
    if (!token) {
        return <Navigate to="/login" replace />;
    }
    
    // Render protected content if authenticated
    return children;
};

/**
 * Main App Component
 * Sets up the routing structure for the entire application
 * Handles protected and public routes
 * 
 * Route Structure:
 * - / : Home page with SavingsTracker
 * - /login : User authentication
 * - /success : Successful payment/registration
 * - /cancel : Cancelled payment/registration
 * - /dashboard : Protected user dashboard
 * - /terms : Terms and Conditions page
 * - * : Catch-all route redirecting to home
 */
function App() {
    return (
        <Router>
            <Routes>
                {/* Public Routes */}
                <Route path="/" element={<SavingsTracker />} />
                <Route path="/login" element={<Login />} />
                <Route path="/success" element={<Success />} />
                <Route path="/cancel" element={<Cancel />} />
                <Route path="/terms" element={<TermsAndConditions />} />
                
                {/* Protected Dashboard Route */}
                <Route 
                    path="/dashboard" 
                    element={
                        <ProtectedRoute>
                            <UserDashboard />
                        </ProtectedRoute>
                    } 
                />
                
                {/* Catch-all Route - Redirects undefined routes to home */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Router>
    );
}

export default App;